import { Map } from 'immutable';
import shortid from 'shortid';
import { createAction, createApiAction } from '@saucelabs/sl-util-redux';
import { actions as notifications } from '@saucelabs/sl-container-notifications';
import { selectors as concurrencySelectors } from '@saucelabs/sl-module-concurrency';
import { actions as userRdcActions } from '@saucelabs/sl-module-user-rdc';
import { actions as rdcTunnelsActions } from '@saucelabs/sl-module-tunnels-rdc';
import { actions as segmentioActions } from '@saucelabs/sl-util-segmentio';
import Raven from '@saucelabs/sl-util-sentry';
import { push } from 'connected-react-router';
import { format } from 'date-fns';
import { saveAs } from 'file-saver';

import * as api from './api';
import * as rdcApi from './rdcApi';
import * as constants from './constants';
import * as utils from './utils';

const MAX_CONFIGS_COUNT = 7;

export const selectDataCenter = createAction(constants.SET_DATACENTER_ID);

const getOverallConcurrency = (concurrency) => {
  const allowed = concurrency.get('allowed', { overall: 0 }).overall;
  const current = concurrency.get('current', { overall: 0 }).overall;

  return allowed - current;
};

export const hasOverallConcurrency = (concurrency) => !!getOverallConcurrency(concurrency);

export const cleanManualPath = (
  comingFromAppSession = false,
) => (comingFromAppSession ? push('/live/app-testing') : push('/live/web-testing'));

export const redirectToDashboard = () => (
  push('/dashboard')
);

export const redirectToManualJob = (jobId) => (
  push(`/live/${jobId}`)
);

/* REQUESTS */
export const getOptions = createApiAction(constants.OPTIONS_GET, () => api.fetchOptions());

export const putRecentConfigs = createApiAction(
  constants.RECENT_CONFIGS_PUT,
  (payload) => api.putMetadata('manual', 'recent_configs', payload),
);

export const putSavedConfigs = createApiAction(
  constants.SAVED_CONFIGS_PUT,
  (payload) => api.putMetadata('manual', 'saved_configs', payload),
);

export const getMetadata = createApiAction(constants.METADATA_GET, () => api.fetchMetadata());
export const putMetadata = createApiAction(constants.METADATA_PUT, ({ page, key, data }) => (
  api.putMetadata(page, key, data)
));

export const createDesktopSession = createApiAction(
  constants.DESKTOP_SESSION_CREATE,
  (payload, meta, dispatch) => api.createSession(payload).then((response) => {
    dispatch(push(`/live/${response.data.task_id}`));
    return response;
  }),
);

export const getDesktopSessionRequest = createAction(constants.DESKTOP_SESSION_GET.REQUEST);
export const getDesktopSessionSuccess = (payload) => ({
  type: constants.DESKTOP_SESSION_GET.SUCCESS,
  payload,
});

export const showSauceConnectRdcPublicDisabled = () => (dispatch) => {
  const { text, header } = constants.SAUCE_CONNECT_PUBLIC_RDC_DISABLED_ERROR;
  const persist = true;
  return dispatch(notifications.notifyWarning(text, header, persist));
};

export const getDesktopSessionFailure = (payload, meta) => ({
  type: constants.DESKTOP_SESSION_GET.FAILURE,
  payload,
  meta,
});

export const getDesktopSession = () => (dispatch, getState) => {
  dispatch(getDesktopSessionRequest());
  const taskId = getState().apps.manual.desktopSession.get('task_id');

  return api.getSession(taskId).then(
    ({ data }) => dispatch(getDesktopSessionSuccess(data)),
    (error) => {
      if (error &&
        error.response &&
        error.response.status >= 400 && error.response.status < 500
      ) {
        const meta = { skipErrorPage: true };
        return dispatch(getDesktopSessionFailure(error, meta));
      }
      return dispatch(getDesktopSessionFailure(error));
    },
  );
};

export const saveDesktopSessionTimeout = createAction(constants.SAVE_DESKTOP_SESSION_TIMEOUT);
export const startSubscribeDesktopSession = createAction(constants.SUBSCRIBE_DESKTOP_SESSION);
export const stopSubscribeDesktopSession = createAction(constants.UNSUBSCRIBE_DESKTOP_SESSION);

let desktopSessionSubscriptionInstance;
export const subscribeDesktopSession = () => (dispatch) => {
  if (desktopSessionSubscriptionInstance) return null;
  dispatch(startSubscribeDesktopSession());

  desktopSessionSubscriptionInstance = () => {
    dispatch(getDesktopSession());
    const timeoutId = setTimeout(
      () => desktopSessionSubscriptionInstance(),
      constants.DESKTOP_SESSION_POLLING_INTERVAL,
    );
    dispatch(saveDesktopSessionTimeout(timeoutId));
  };
  return desktopSessionSubscriptionInstance();
};

export const unsubscribeDesktopSession = () => (dispatch) => {
  dispatch(stopSubscribeDesktopSession());
  desktopSessionSubscriptionInstance = null;
};

export const getDesktopSessionJob = createApiAction(
  constants.DESKTOP_SESSION_JOB_GET,
  (jobId) => api.getJob(jobId, true),
);

export const updateDesktopSessionInitialJob = (payload) => ({
  type: constants.DESKTOP_SESSION_JOB_INITIAL_UPDATE,
  payload,
});

export const toggleDesktopSessionAutoLaunch = createAction(
  constants.DESKTOP_SESSION_AUTO_LAUNCH_TOGGLE,
);
export const delayDesktopSession = (payload) => ({
  type: constants.DESKTOP_SESSION_DELAY,
  payload,
});

/* END NEW UI */

// Mobile

export const fetchRdcSubscriptionAndSubscribeRdcTunnels = createApiAction(
  constants.RDC_TUNNELS_SUBSCRIBE,
  (payload, meta, dispatch, getState) => dispatch(userRdcActions.getRdcSubscription())
    .then(() => {
      const { userRdc } = getState();
      if (userRdc.get('subscription').isSauceConnectEnabled()) {
        dispatch(rdcTunnelsActions.subscribeTunnels());
      }
    }),
);

export const cleanupMobileDevicesFacets = createAction(
  constants.MOBILE_DEVICES_FACETS_CLEANUP,
);

export const fetchMobileDevices = createApiAction(
  constants.MOBILE_DEVICES_FETCH,
  (payload) => rdcApi.fetchFilteredDevices(payload),
  { meta: { skipErrorPage: true } },
);

export const fetchMobileDevice = createApiAction(
  constants.MOBILE_DEVICE_FETCH,
  ({ dataCenterId, deviceId }) => rdcApi.fetchDevice({ dataCenterId, deviceId }),
  { meta: { skipErrorPage: true } },
);

export const fetchAvailableDeviceIds = createApiAction(
  constants.MOBILE_AVAILABLE_DEVICE_IDS_FETCH,
  (dataCenterId) => rdcApi.fetchAllAvailableDeviceIds(dataCenterId),
  { meta: { skipErrorPage: true } },
);

export const fetchMobileSessionAvailable = createApiAction(
  constants.MOBILE_SESSION_AVAILABLE_FETCH,
  () => rdcApi.fetchSessionAvailable(),
  { meta: { skipErrorPage: true } },
);

export const openMobileDeviceTileDetails = createAction(
  constants.MOBILE_DEVICE_TILE_DETAILS_OPEN,
);

export const closeMobileDeviceTileDetails = createAction(
  constants.MOBILE_DEVICE_TILE_DETAILS_CLOSE,
);

function getRdcOwnerId(getState) {
  const { userRdc } = getState();
  return userRdc.get('parentId') || userRdc.get('id');
}

export const getOrFetchRdcOwnerId = createApiAction(
  constants.RDC_OWNER_ID_FETCH,
  (payload, meta, dispatch, getState) => {
    const rdcOwnerId = getRdcOwnerId(getState);

    if (rdcOwnerId) {
      return Promise.resolve(rdcOwnerId);
    }

    return dispatch(userRdcActions.getWhoamiRdc())
      .then(({ data }) => data)
      .catch((error) => {
        dispatch(notifications.notifyDanger('We had some trouble authenticating you'));
        throw error;
      });
  },
  { meta: { skipErrorPage: true } },
);


let uploadAppId = 0;
const uploadAppCancelTokens = {};
export const uploadAppProgress = createAction(constants.MOBILE_APP_UPLOAD.PROGRESS);
export const uploadAppCancel = (id) => (dispatch) => {
  uploadAppCancelTokens[id](constants.APP_UPLOAD_CANCEL_MESSAGE);
  delete uploadAppCancelTokens[id];
  dispatch(notifications.notifyDanger(constants.APP_UPLOAD_CANCEL_MESSAGE));
};
export const uploadApp = (...args) => {
  uploadAppId += 1;

  return createApiAction(
    constants.MOBILE_APP_UPLOAD,
    (app, meta, dispatch) => (
      rdcApi.uploadApp(
        app,
        (progressEvent) => dispatch(
          uploadAppProgress(
            Math.round(progressEvent.loaded / progressEvent.total) * 100,
            meta,
          ),
        ),
        (cancelToken) => {
          uploadAppCancelTokens[meta.uploadAppId] = cancelToken;
        },
      ))
      .finally(() => {
        delete uploadAppCancelTokens[meta.uploadAppId];
      }),
    {
      meta: {
        uploadAppId,
        skipErrorPage: true,
      },
    },
  )(...args);
};

export const fetchMobileApps = createApiAction(
  constants.MOBILE_APPS_FETCH,
  (payload, meta, dispatch) => rdcApi.fetchApps(payload)
    // TODO handle pagination (gs)
    .then(({ data }) => data.entities)
    .catch((error) => {
      dispatch(notifications.notifyDanger(error));
      throw error;
    }),
  { meta: { skipErrorPage: true } },
);

export const fetchMobileAppVersion = createApiAction(
  constants.MOBILE_SESSION_APP_VERSION_FETCH,
  ({ appId }, meta, dispatch) => dispatch(getOrFetchRdcOwnerId())
    .then(() => rdcApi.fetchAppVersion(appId))
    .catch((error) => {
      dispatch(notifications.notifyDanger(error));
      throw error;
    }),
  { meta: { skipErrorPage: true } },
);

export const fetchDeviceState = (dispatch, getState) => {
  const dataCenterId = getState().apps.manual.rdcDevicePicker.get('lastDataCenterId');
  dispatch(fetchAvailableDeviceIds(dataCenterId));
  dispatch(fetchMobileSessionAvailable());
};

let pollingTimerId;
const pollDeviceState = (dispatch, getState) => {
  fetchDeviceState(dispatch, getState);
  pollingTimerId = setInterval(fetchDeviceState.bind(this, dispatch, getState), 3000);
};

export const getDeviceGridInitialData = ({ user, os, dataCenterId }) => (dispatch) => (
  userRdcActions.debouncedEnsureRdcUser(dispatch, { user })
    .then(() => {
      dispatch(fetchRdcSubscriptionAndSubscribeRdcTunnels());
      return dispatch(fetchMobileDevices({ os: [os], dataCenterId }))
        .then(dispatch(pollDeviceState));
    })
);

export const unloadDeviceGrid = () => (dispatch) => {
  dispatch(cleanupMobileDevicesFacets());
  clearInterval(pollingTimerId);
  dispatch(rdcTunnelsActions.unsubscribeTunnels());
};

export const getRdcUserData = (user) => (dispatch) => (
  userRdcActions.debouncedEnsureRdcUser(dispatch, { user })
);

export const getMobileAppsInitialData = ({
  user,
  searchTerm,
  osFilters,
}) => (dispatch) => (
  userRdcActions.debouncedEnsureRdcUser(dispatch, { user })
    .then(() => {
      dispatch(fetchMobileApps({ searchTerm, osFilters }));
    })
);

export const connectMobileDevice = createApiAction(
  constants.MOBILE_DEVICE_CONNECT,
  ({ dataCenterId, deviceSessionId, elementId }, meta, dispatch) => (
    rdcApi.connectDevice({ dataCenterId, deviceSessionId, elementId })
      .then((device) => {
        device.setMultiTouchFailureCallback(
          ({ message }) => dispatch(notifications.notifyWarning(message)),
        );
        return device;
      })
      .catch((error) => {
        dispatch(notifications.notifyDanger(error));
        throw error;
      })
  ),
  { meta: { skipErrorPage: true } },
);

const handleRdcDeviceResponse = (data) => {
  if (data.status === 'ERROR') {
    throw data.error;
  }

  return data;
};

export const getCreateMobileSessionError = (error) => {
  const {
    CREATE_MOBILE_SESSION_ERRORS,
    CREATE_MOBILE_SESSION_ERROR_MESSAGES,
    CREATE_MOBILE_SESSION_ERROR_MESSAGE_DEFAULT,
  } = constants;

  switch (error) {
    case CREATE_MOBILE_SESSION_ERRORS[error]:
      return CREATE_MOBILE_SESSION_ERROR_MESSAGES[error];
    default:
      return CREATE_MOBILE_SESSION_ERROR_MESSAGE_DEFAULT;
  }
};


/* DESKTOP MANUAL SESSION */

export const closeDesktopSession = createApiAction(
  constants.DESKTOP_SESSION_CLOSE,
  ({ taskId }, meta, dispatch) => api.removeSession(taskId)
    .then((data) => {
      dispatch(cleanManualPath(false));
      return data;
    })
    .catch((error) => {
      Raven.captureException(error);
      dispatch(cleanManualPath(false));
      throw error;
    }),
);

export const takeDesktopSessionScreenshot = createApiAction(
  constants.DESKTOP_SESSION_TAKE_SCREENSHOT,
  ({ taskId }, meta, dispatch) => api.takeScreenshot(taskId).then(
    ({ data }) => {
      dispatch(notifications.notifySuccess('Screenshot saved to the Test Details page.'));
      return data;
    },
    (error) => {
      dispatch(notifications.notifyDanger('Failed to take a screenshot.'));
      throw error;
    },
  ),
);


/* MOBILE MANUAL SESSION */

export const createMobileSession = createApiAction(
  constants.MOBILE_SESSION_CREATE,
  ({ dataCenterId, deviceId, elementId, appId, tunnelIdentifier }, meta, dispatch) => {
    const isDeprecatedAppId = typeof appId === 'string';
    let method;

    // deprecated RDCWEB-3542
    if (isDeprecatedAppId) {
      method = rdcApi.createAppSession;
    } else {
      method = appId ? rdcApi.createAppSessionExperiment : rdcApi.createBrowserSession;
    }

    return method({ dataCenterId, deviceId, appId, tunnelIdentifier })
      .then((response) => {
        const deviceSessionId = (typeof response === 'string') ? response : response.deviceSessionId;
        dispatch(connectMobileDevice({ dataCenterId, deviceSessionId, elementId }));
      })
      .catch((error) => {
        dispatch(notifications.notifyDanger(getCreateMobileSessionError(error)));
        throw error;
      });
  },
  { meta: { skipErrorPage: true } },
);

export const openUrlInMobileDevice = createApiAction(
  constants.MOBILE_DEVICE_OPEN_URL,
  (url, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');

    return device.openUrl(url).then(handleRdcDeviceResponse);
  },
  { meta: { skipErrorPage: true } },
);

export const installMobileApp = createApiAction(
  constants.MOBILE_DEVICE_APP_INSTALL,
  (appId, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');

    return device.installAndLaunchApp(appId).then(handleRdcDeviceResponse);
  },
  { meta: { skipErrorPage: true } },
);

export const pasteContentMobileDevice = createApiAction(
  constants.MOBILE_DEVICE_PASTE_CONTENT,
  (payload, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');

    return Promise.all([
      device.pasteText(payload),
      dispatch(notifications.notifySuccess('Pasting content...')),
    ]);
  },
  { meta: { skipErrorPage: true } },
);

export const executeADBShell = createApiAction(
  constants.MOBILE_DEVICE_EXECUTE_ADB_SHELL,
  (payload, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');

    return Promise.all([
      device.executeShellCommand(payload),
      dispatch(notifications.notifySuccess('Executing ADB shell command...')),
      // eslint-disable-next-line no-console
    ]).then(([message]) => console.log(`ADB shell output: "${message.text()}"`));
  },
  { meta: { skipErrorPage: true } },
);

export const restartMobileDeviceApp = createApiAction(
  constants.MOBILE_DEVICE_APP_RESTART,
  (payload, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');

    return Promise.all([
      device.relaunchCurrentApp(),
      dispatch(notifications.notifySuccess('Restarting your app...')),
    ]);
  },
  { meta: { skipErrorPage: true } },
);

export const toggleMobileDeviceAnimations = createApiAction(
  constants.MOBILE_DEVICE_ANIMATIONS_TOGGLE,
  (payload, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');
    const animationsEnabled = getState().apps.manual.session.get('deviceAnimationsEnabled');

    const methodName = animationsEnabled ? 'disableAnimations' : 'enableAnimations';

    return device[methodName]().then(() => (
      dispatch(notifications.notifySuccess(`Animations successfully ${animationsEnabled ? 'disabled' : 'enabled'}.`))
    ));
  },
  { meta: { skipErrorPage: true } },
);

export const toggleMobileDeviceWifi = createApiAction(
  constants.MOBILE_DEVICE_WIFI_TOGGLE,
  (payload, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');
    const wifiEnabled = getState().apps.manual.session.get('deviceWifiEnabled');

    const methodName = wifiEnabled ? 'disableWifi' : 'enableWifi';

    return device[methodName]().then(() => (
      dispatch(notifications.notifySuccess(`WiFi successfully ${wifiEnabled ? 'disabled' : 'enabled'}.`))
    ));
  },
  { meta: { skipErrorPage: true } },
);

export const performBiometricsAuthentication = createApiAction(
  constants.MOBILE_DEVICE_BIOMETRICS_AUTHENTICATION,
  (payload, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');
    const { match } = payload;

    return device.performBiometricsAuthentication(match).then((response) => {
      // TODO: Let request errors fail in `web-rdc-client` to enable handling server errors.
      // Right now the response only has a mesage when the request fails (i.e. 500 status).
      if (response.message) {
        return dispatch(notifications.notifyWarning('A biometric authentication reponse has been sent but could not be handled properly. Make sure your app is open.'));
      }
      return dispatch(notifications.notifySuccess(`A ${match ? 'successful' : 'failed'} biometric authentication response has been sent to your app.`));
    });
  },
);

export const fetchMobileDeviceLanguages = createApiAction(
  constants.MOBILE_DEVICE_LANGUAGES_FETCH,
  (payload, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');

    return device.getLocales();
  },
  { meta: { skipErrorPage: true } },
);

export const setMobileDeviceLanguage = createApiAction(
  constants.MOBILE_DEVICE_LANGUAGE_SET,
  (payload, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');

    return device.setLocale(payload.id).then((updatedLocale) => (
      dispatch(notifications.notifySuccess(`Language successfully set to ${updatedLocale.label}`))
    ));
  },
  { meta: { skipErrorPage: true } },
);

export const changeMobileDeviceGPSLocation = createAction(
  constants.MOBILE_DEVICE_GPS_LOCATION_CHANGE,
);

export const setMobileDeviceGPSLocation = createApiAction(
  constants.MOBILE_DEVICE_GPS_LOCATION_SET,
  (payload, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');

    return device.setGps(payload.latitude, payload.longitude).then((updatedLocation) => {
      dispatch(notifications.notifySuccess(`GPS successfully set to ${updatedLocation.latitude}, ${updatedLocation.longitude}.`));
    });
  },
  { meta: { skipErrorPage: true } },
);

export const rotateMobileDeviceRequest = createAction(constants.MOBILE_DEVICE_ROTATE.REQUEST);
export const rotateMobileDeviceSuccess = createAction(constants.MOBILE_DEVICE_ROTATE.SUCCESS);
export const rotateMobileDevice = () => (dispatch, getState) => {
  const device = getState().apps.manual.session.get('device');

  return new Promise((resolve) => {
    device.onRotationStart((orientation) => dispatch(rotateMobileDeviceRequest(orientation)));
    device.onRotationFinish((orientation) => {
      dispatch(rotateMobileDeviceSuccess(orientation));
      resolve();
    });

    device.rotate();
  });
};

export const shareMobileDeviceSession = createApiAction(
  constants.MOBILE_DEVICE_SHARE_SESSION,
  (payload, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');
    return device.share();
  },
  { meta: { skipErrorPage: true } },
);

export const pressMobileDeviceHardwareButton = createApiAction(
  constants.MOBILE_DEVICE_HARDWARE_BUTTON_PRESS,
  (hardwareButtonType, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');

    switch (hardwareButtonType) {
      case constants.MOBILE_DEVICE_HARDWARE_BUTTON_TYPES.BACK:
        return Promise.resolve(device.pressBack());
      case constants.MOBILE_DEVICE_HARDWARE_BUTTON_TYPES.HOME:
        return Promise.resolve(device.pressHome());
      case constants.MOBILE_DEVICE_HARDWARE_BUTTON_TYPES.MENU:
        return Promise.resolve(device.pressMenu());
      case constants.MOBILE_DEVICE_HARDWARE_BUTTON_TYPES.SEARCH:
        return Promise.resolve(device.pressSearch());
      default:
        throw new Error(`Unknown hardware button type '${hardwareButtonType}'`);
    }
  },
  { meta: { skipErrorPage: true } },
);

export const showMobileDrawer = createAction(constants.MOBILE_DRAWER_SHOW);
export const setMobileDrawerHeight = createAction(constants.MOBILE_DRAWER_SET_HEIGHT);
export const setMobileDrawerWidth = createAction(constants.MOBILE_DRAWER_SET_WIDTH);

export const showLog = createAction(constants.MOBILE_LOG_SHOW);
export const showVitals = createAction(constants.MOBILE_VITALS_SHOW);
export const changeVitalsGraphHeight = createAction(constants.MOBILE_VITALS_GRAPH_HEIGHT_CHANGE);
export const changeVitalsLayout = createAction(constants.MOBILE_VITALS_LAYOUT_FULLWIDTH_CHANGE);
export const updateVisibleVitalsData = createAction(constants.MOBILE_VITALS_UPDATE_VISIBLE_DATA);
export const updateMobileDeviceState = createAction(constants.MOBILE_DEVICE_STATE_UPDATE);

export const takeMobileDeviceScreenshot = createApiAction(
  constants.MOBILE_DEVICE_TAKE_SCREENSHOT,
  (payload, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');

    return device.getScreenshotUrl()
      .then(({ url }) => {
        /* it fires download a file */
        window.open(url, '_blank');
      })
      .catch((error) => {
        dispatch(notifications.notifyDanger(error));
        throw error;
      });
  },
  { meta: { skipErrorPage: true } },
);

export const stopMobileSession = createApiAction(
  constants.MOBILE_SESSION_STOP,
  (payload, meta, dispatch, getState) => {
    const device = getState().apps.manual.session.get('device');

    return new Promise((resolve) => {
      if (device) {
        device.close();

        window.removeEventListener('resize', device.resize);
        window.removeEventListener('beforeunload', device.close);
      }
      resolve();
    });
  },
  { meta: { skipErrorPage: true } },
);

export const addMobileLogLines = createAction(constants.MOBILE_LOG_LINE_ADD);
export const addMobileVitalsEntry = createAction(constants.MOBILE_VITALS_ENTRY_ADD);
export const loadMoreMobileLogLines = createAction(constants.MOBILE_LOG_LINES_LOAD_MORE);
export const pauseMobileLogLines = createAction(constants.MOBILE_LOG_LINES_PAUSE);
export const resumeMobileLogLines = createAction(constants.MOBILE_LOG_LINES_RESUME);
export const setMobileLogLevel = createAction(constants.MOBILE_LOG_SET_LOG_LEVEL);
export const clearMobileLog = createAction(constants.MOBILE_LOG_CLEAR);
export const updateDisplayedMobileLogLines = createAction(
  constants.MOBILE_LOG_UPDATE_DISPLAYED_LINES,
);
export const updateMobileLogFilters = createAction(constants.MOBILE_LOG_UPDATE_FILTERS);

export const downloadMobileLogAction = createAction(constants.MOBILE_LOG_DOWNLOAD_LOG);
export const downloadMobileLog = ({ deviceId }) => (dispatch, getState) => {
  dispatch(downloadMobileLogAction({ deviceId }));
  const allLines = getState().apps.manual.log.get('lines');

  const filename = `SauceLabs_Log_${deviceId}_${format(new Date(), constants.MOBILE_LOG_DATETIME_SHORT_FORMAT)}.txt`;
  const blob = new Blob(
    allLines.map(
      (line) => `${format(new Date(line.timestamp), constants.MOBILE_LOG_DATETIME_FORMAT)}\t${line.level}:\t${line.message}\n`,
    ),
    { type: 'text/plain;charset=utf-8' },
  );

  return saveAs(blob, filename);
};

export const downloadMobileVitalsAction = createAction(constants.MOBILE_VITALS_DOWNLOAD_LOG);
export const downloadMobileVitals = ({ deviceId }) => (dispatch, getState) => {
  dispatch(downloadMobileVitalsAction({ deviceId }));
  const allLines = getState().apps.manual.vitals.get('csvData');

  if (!allLines.size) {
    return dispatch(notifications.notifyWarning('No device vitals data has been collected yet.'));
  }

  const filename = `SauceLabs_Vitals_${deviceId}_${format(new Date(), constants.MOBILE_LOG_DATETIME_SHORT_FORMAT)}.txt`;
  const blob = new Blob(
    allLines.map(
      (line) => `${line}\n`,
    ),
    { type: 'text/plain;charset=utf-8' },
  );
  return saveAs(blob, filename);
};

export const getInitialData = createApiAction(
  constants.INITIAL_DATA,
  (payload, meta, dispatch) => Promise.all([
    dispatch(getOptions()),
    dispatch(getMetadata()),
  ]),
);

export const handleCreateSession = (payload, dispatch) => {
  const { platformType, device, deviceType } = payload;
  const { FORM_TYPES: { DESKTOP_BROWSER, VIRTUAL_DEVICE, REAL_DEVICE, MOBILE_APP } } = constants;
  const formType = utils.getFormType(platformType, device, deviceType);

  if (formType === DESKTOP_BROWSER) {
    const desktopPayload = {
      url: payload.url,
      chosenBrowser: payload.browserConfiguration.browserDisplay,
      chosenBrowserVersion: payload.browserConfiguration.browserVersionDisplay,
      resolution: payload.browserConfiguration.res,
      os: payload.browserConfiguration.osVersionDisplay,
      sauceConnectProxy: payload.tunnel || 'null',
    };
    dispatch(segmentioActions.recordTrack('Desktop Test Launched', desktopPayload));

    return dispatch(createDesktopSession({
      ...payload,
      configuration: payload.browserConfiguration,
    }));
  }

  if (formType === VIRTUAL_DEVICE) {
    const deviceConfig = payload.virtualDeviceConfiguration;
    const virtualDevicePayload = {
      deviceType: 'Virtual Devices',
      sauceConnectProxy: payload.tunnel || 'null',
      device: deviceConfig.device,
      os: deviceConfig.version,
    };
    dispatch(segmentioActions.recordTrack('Virtual Device Test Launched', virtualDevicePayload));

    return dispatch(createDesktopSession({
      ...payload,
      configuration: payload.virtualDeviceConfiguration,
    }));
  }

  if (formType === REAL_DEVICE) {
    const { deviceConfiguration, url, tunnelIdentifier } = payload;
    const realDevicePayload = {
      deviceType: 'Real Devices',
      deviceId: payload.id,
      sauceConnectProxy: payload.tunnelIdentifier || 'null',
    };
    dispatch(segmentioActions.recordTrack('Real Device Test Launched', realDevicePayload));

    return dispatch(
      push(
        `/live/mobile/dataCenters/${deviceConfiguration.dataCenterId}/devices/${deviceConfiguration.descriptorId}`,
        {
          url,
          tunnelIdentifier,
        },
      ),
    );
  }

  if (formType === MOBILE_APP) {
    const { deviceConfiguration, mobileAppConfiguration, tunnelIdentifier } = payload;
    return dispatch(
      push(
        `/live/mobile/dataCenters/${deviceConfiguration.dataCenterId}/devices/${deviceConfiguration.descriptorId}`,
        {
          appId: mobileAppConfiguration.id, // `appId` could be a string (`projectId` deprecated) or a number.
          appVersion: mobileAppConfiguration.defaultApp, // only used in case `appId` is a string.
          tunnelIdentifier,
        },
      ),
    );
  }

  return Promise.resolve();
};

export const handleTabsFormSubmit = (payload) => (dispatch, getState) => {
  const { apps: { manual } } = getState();

  const { concurrency } = concurrencySelectors.concurrencySelector()(getState());
  const overallConcurrency = hasOverallConcurrency(concurrency);

  const fixedPayload = {
    ...payload,
    url: payload.url ? payload.url.trim() : constants.SESSION_DEFAULT_URL,
  };

  const recentConfigs = manual.metadata.get('recent_configs');
  const savedConfigs = manual.metadata.get('saved_configs');
  const configObject = utils.getLiveTestingConfig(fixedPayload);
  const newConfig = configObject && new Map({
    ...configObject,
    id: shortid.generate(),
  });

  if (overallConcurrency) {
    handleCreateSession(fixedPayload, dispatch);

    if (newConfig) {
      dispatch(putRecentConfigs(
        recentConfigs
          .insert(0, newConfig)
          .slice(0, MAX_CONFIGS_COUNT),
      ));
    }

    if (fixedPayload.saveConfiguration && newConfig) {
      dispatch(putSavedConfigs(
        savedConfigs
          .insert(0, newConfig)
          .slice(0, MAX_CONFIGS_COUNT),
      ));
    }
  } else {
    dispatch(delayDesktopSession(fixedPayload));
  }
};

export const getJobIdBasedOnTaskId = createApiAction(
  constants.JOB_ID_BASED_ON_TASK_ID_GET,
  api.getJobIdBasedOnTaskId,
);

export const setFilterItems = createAction(constants.SET_FILTER_ITEMS);
